import { useMemo } from "react";
import axiosInstance from "../services/axiosInstance";
import { API_ENDPOINTS } from "../services/api/apiConfig";
import type { Constante, Doctor, Patient } from "../types";
import { useQuery } from "@tanstack/react-query";

export const useConstanteData = (
  _role: "admin" | "doctor", // Keep role for compatibility if needed, though unused in logic now
  constanteId?: string
) => {
  const doctorsQuery = useQuery({
    queryKey: ['doctors', 'sorted'],
    queryFn: async () => {
      const response = await axiosInstance.get(API_ENDPOINTS.USERS.DOCTORS.SORTED);
      return response.data as Doctor[];
    },
  });

  const patientsQuery = useQuery({
    queryKey: ['patients', 'list'],
    queryFn: async () => {
      const response = await axiosInstance.get(API_ENDPOINTS.PATIENTS.LIST);
      return response.data as Patient[];
    },
  });

  const constanteIdNum = constanteId ? Number(constanteId) : undefined;

  const constanteQuery = useQuery({
    queryKey: ['constante', constanteIdNum],
    queryFn: async () => {
      if (!constanteIdNum) return null;
      const response = await axiosInstance.get(API_ENDPOINTS.CONSTANTES.GET_BY_ID(constanteIdNum));
      const constanteData = response.data;

      // Data enrichment
      const enrichedConstante: Constante = {
        ...constanteData,
        fullName:
          constanteData.fullName ||
          (constanteData.patient
            ? `${constanteData.patient.firstName || ""} ${constanteData.patient.lastName || ""}`.trim()
            : `${constanteData.firstName || ""} ${constanteData.lastName || ""}`.trim()),
        doctorName:
          constanteData.doctorName ||
          (constanteData.doctor
            ? `Dr. ${constanteData.doctor.firstName} ${constanteData.doctor.lastName}`.trim()
            : undefined),
        phone: constanteData.phone || constanteData.patient?.phone,
        poids: constanteData.poids || constanteData.weight,
        taille: constanteData.taille || constanteData.height,
        tensionArterielle: constanteData.tensionArterielle || constanteData.bloodPressure,
        frequenceCardiaque:
          constanteData.frequenceCardiaque ||
          constanteData.heartRate ||
          constanteData.rythmeCardiaque,
        temperature: constanteData.temperature,
        motif: constanteData.motif || constanteData.reason,
      };
      return enrichedConstante;
    },
    enabled: !!constanteIdNum,
  });

  const loading = doctorsQuery.isLoading || patientsQuery.isLoading || (!!constanteIdNum && constanteQuery.isLoading);

  const error = useMemo(() => {
    const err = doctorsQuery.error || patientsQuery.error || constanteQuery.error;
    if (!err) return null;

    const axiosErr = err as any;
    if (axiosErr.response?.status === 403) return "Accès refusé. Veuillez vous reconnecter.";
    if (axiosErr.response?.status === 404) return "Constante non trouvée.";
    return `Erreur serveur: ${axiosErr.response?.status || "Inconnue"}`;
  }, [doctorsQuery.error, patientsQuery.error, constanteQuery.error]);

  return {
    doctors: doctorsQuery.data || [],
    patients: patientsQuery.data || [],
    constante: constanteQuery.data || undefined,
    loading,
    error
  };
};

